import React, { useState, useEffect, useRef } from "react";
import "./HomeScreen.css";
import { useLanguage } from "../contexts/LanguageContext";
import { useTheme, MODE_LABEL_KEYS } from "../contexts/ThemeContext";
import { useFirebase } from "../contexts/FirebaseContext";
import { GAMES } from "../constants/games";
import HighScores from "./HighScores";
import MazeHighScores from "./MazeHighScores";
import OpsTicklesHighScores from "./OpsTicklesHighScores";
import SolitaireHighScores from "./SolitaireHighScores";
import MissionsPage from "./MissionsPage";
import RewardsPage from "./RewardsPage";
import HowToPlayPage from "./HowToPlayPage";
import PixelsPage from "./PixelsPage";
import LanguagesPage from "./LanguagesPage";
import {
  getFilamentHex,
  getFilamentLabel,
  isFilamentLight,
  isFilamentRainbow,
} from "../constants/filamentColors";
import {
  StarworksLogo,
  HomeIcon,
  TrophyIcon,
  GiftIcon,
  TargetIcon,
  HelpIcon,
  PixelsIcon,
  LanguagesIcon,
  StarIcon,
  StarIcon3D,
  SearchIcon,
  AvatarIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ArrowRightIcon,
  FlameIcon,
  GamesPlayedIcon,
  HangmanIcon,
  FillerIcon,
  FlashcardsIcon,
  DinoIcon,
  MazeIcon,
  OpsTicklesIcon,
  ChessIcon,
  MemoryIcon,
  SolitaireIcon,
} from "./HomeIcons";

const EARN_GAMES = ["hangman", "filler", "flashcards"];
const SPEND_GAMES = ["dino", "maze", "opsTickles", "chess", "memory", "solitaire"];
const ALL_HOME_GAMES = [...EARN_GAMES, ...SPEND_GAMES];
const EARN_SCROLL_STEP = 336;

const GAME_ICON_MAP = {
  hangman: { icon: HangmanIcon, accent: "hangman" },
  filler: { icon: FillerIcon, accent: "filler" },
  flashcards: { icon: FlashcardsIcon, accent: "flashcards" },
  dino: { icon: DinoIcon, accent: "dino" },
  maze: { icon: MazeIcon, accent: "maze" },
  opsTickles: { icon: OpsTicklesIcon, accent: "opsTickles" },
  chess: { icon: ChessIcon, accent: "chess" },
  memory: { icon: MemoryIcon, accent: "memory" },
  solitaire: { icon: SolitaireIcon, accent: "solitaire" },
};

const formatClaimDate = (value) => {
  if (!value) return "—";
  try {
    return value.toLocaleDateString();
  } catch {
    return "—";
  }
};

const highlightMatch = (text, query) => {
  const needle = query.trim();
  if (!needle) return text;

  const lowerText = text.toLowerCase();
  const lowerNeedle = needle.toLowerCase();
  const index = lowerText.indexOf(lowerNeedle);
  if (index === -1) return text;

  return (
    <>
      {text.slice(0, index)}
      <mark className="sw-search-mark">
        {text.slice(index, index + needle.length)}
      </mark>
      {text.slice(index + needle.length)}
    </>
  );
};

const HomeScreen = ({
  onSelectGame,
  onOpenHowTo,
  howToRequest,
  points,
  displayName,
  onEditDisplayName,
  language,
  onChangeLanguage,
  musicPlaying,
  onToggleMusic,
}) => {
  const { t } = useLanguage();
  const { mode, modes, modeSwatches, changeMode } = useTheme();
  const swatches = modeSwatches || {};
  const {
    getUserRewardClaims,
    deleteRewardClaim,
    gameProgress,
    submitProblemReport,
  } = useFirebase();
  const [activeNav, setActiveNav] = useState("home");
  const [howToGameId, setHowToGameId] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchMatches, setSearchMatches] = useState([]);
  const [searchFocused, setSearchFocused] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [modeMenuOpen, setModeMenuOpen] = useState(false);
  const [shipmentsOpen, setShipmentsOpen] = useState(false);
  const [shipments, setShipments] = useState([]);
  const [shipmentsLoading, setShipmentsLoading] = useState(false);
  const [shipmentsError, setShipmentsError] = useState("");
  const [deletingShipmentId, setDeletingShipmentId] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportMessage, setReportMessage] = useState("");
  const [reportSending, setReportSending] = useState(false);
  const [reportError, setReportError] = useState("");
  const [reportSuccess, setReportSuccess] = useState("");
  const [canScrollEarnLeft, setCanScrollEarnLeft] = useState(false);
  const [canScrollEarnRight, setCanScrollEarnRight] = useState(false);
  const menuRef = useRef(null);
  const searchRef = useRef(null);
  const earnScrollRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) {
      setModeMenuOpen(false);
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        if (modeMenuOpen) {
          setModeMenuOpen(false);
        } else {
          setMenuOpen(false);
        }
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [menuOpen, modeMenuOpen]);

  useEffect(() => {
    if (!howToRequest?.gameId) return;
    setHowToGameId(howToRequest.gameId);
    setActiveNav("howto");
  }, [howToRequest]);

  useEffect(() => {
    if (!searchFocused) return undefined;

    const handlePointerDown = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setSearchFocused(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
    };
  }, [searchFocused]);

  useEffect(() => {
    if (!searchOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setSearchOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [searchOpen]);

  useEffect(() => {
    if (!shipmentsOpen && !reportOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setShipmentsOpen(false);
        if (!reportSending) setReportOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [shipmentsOpen, reportOpen, reportSending]);

  useEffect(() => {
    if (activeNav !== "home") return undefined;

    const el = earnScrollRef.current;
    if (!el) return undefined;

    const updateScrollState = () => {
      const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth);
      const left = el.scrollLeft;
      const atStart = left <= 1;
      const atEnd = left >= maxScroll - 1;
      setCanScrollEarnLeft(maxScroll > 1 && !atStart);
      setCanScrollEarnRight(maxScroll > 1 && !atEnd);
    };

    updateScrollState();
    // Layout can settle after first paint (fonts/cards)
    const rafId = requestAnimationFrame(updateScrollState);
    const timeoutId = setTimeout(updateScrollState, 100);

    el.addEventListener("scroll", updateScrollState, { passive: true });
    el.addEventListener("scrollend", updateScrollState);
    window.addEventListener("resize", updateScrollState);

    let resizeObserver;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(updateScrollState);
      resizeObserver.observe(el);
    }

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timeoutId);
      el.removeEventListener("scroll", updateScrollState);
      el.removeEventListener("scrollend", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
      resizeObserver?.disconnect();
    };
  }, [activeNav]);

  const scrollEarnGames = (direction) => {
    const el = earnScrollRef.current;
    if (!el) return;
    const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth);
    if (direction < 0 && el.scrollLeft <= 1) return;
    if (direction > 0 && el.scrollLeft >= maxScroll - 1) return;
    const card = el.querySelector(".sw-game-card");
    const step = card
      ? card.getBoundingClientRect().width + 16
      : EARN_SCROLL_STEP;
    el.scrollBy({ left: direction * step, behavior: "smooth" });
  };

  const openProblemReport = () => {
    setMenuOpen(false);
    setReportOpen(true);
    setReportError("");
    setReportSuccess("");
  };

  const handleSubmitProblemReport = async (event) => {
    event.preventDefault();
    const trimmed = reportMessage.trim();
    if (!trimmed) {
      setReportError(t("reportProblemRequired"));
      return;
    }
    if (trimmed.length > 2000) {
      setReportError(t("reportProblemTooLong"));
      return;
    }

    setReportSending(true);
    setReportError("");
    setReportSuccess("");
    try {
      await submitProblemReport(trimmed);
      setReportMessage("");
      setReportSuccess(t("reportProblemSuccess"));
    } catch (err) {
      console.error(err);
      setReportError(
        err?.code === "message-too-long"
          ? t("reportProblemTooLong")
          : t("reportProblemError")
      );
    } finally {
      setReportSending(false);
    }
  };

  const openShipments = async () => {
    setMenuOpen(false);
    setShipmentsOpen(true);
    setShipmentsLoading(true);
    setShipmentsError("");
    try {
      const claims = await getUserRewardClaims();
      setShipments(claims);
    } catch (err) {
      console.error(err);
      setShipmentsError(t("shipmentsError"));
    } finally {
      setShipmentsLoading(false);
    }
  };

  const statusLabel = (status) => {
    if (status === "shipped" || status === "fulfilled") {
      return t("shipmentStatusShipped");
    }
    if (status === "on_the_way") return t("shipmentStatusOnTheWay");
    return t("shipmentStatusPending");
  };

  const handleDeleteShipment = async (claim) => {
    const confirmed = window.confirm(t("shipmentDeleteConfirm"));
    if (!confirmed) return;

    setDeletingShipmentId(claim.id);
    setShipmentsError("");
    try {
      await deleteRewardClaim(claim.id);
      setShipments((current) => current.filter((item) => item.id !== claim.id));
    } catch (err) {
      console.error(err);
      setShipmentsError(t("shipmentDeleteError"));
    } finally {
      setDeletingShipmentId(null);
    }
  };

  const navItems = [
    { id: "home", icon: HomeIcon, label: t("navHome") },
    { id: "leaderboard", icon: TrophyIcon, label: t("navLeaderboard") },
    { id: "rewards", icon: GiftIcon, label: t("navRewards") },
    { id: "missions", icon: TargetIcon, label: t("navMissions") },
    { id: "howto", icon: HelpIcon, label: t("navHowToPlay") },
    { id: "pixels", icon: PixelsIcon, label: t("navPixels") },
    { id: "languages", icon: LanguagesIcon, label: t("navLanguages") },
  ];

  // Real progress from gameplay (missions + stats bar).
  const dayStreak = gameProgress?.dayStreak || 0;
  const gamesPlayed = Object.values(gameProgress?.gamesPlayed || {}).reduce(
    (sum, count) => sum + (count || 0),
    0
  );
  const totalStars = points;
  const nextRewardThreshold = 400;
  const starsToReward = Math.max(0, nextRewardThreshold - (points % nextRewardThreshold));
  const rewardProgress =
    nextRewardThreshold > 0
      ? Math.min(100, Math.round(((points % nextRewardThreshold) / nextRewardThreshold) * 100))
      : 0;

  const handleStartPlaying = () => {
    onSelectGame(ALL_HOME_GAMES[0]);
  };

  const handleNavClick = (id) => {
    setActiveNav(id);
    if (id !== "howto") {
      setHowToGameId(null);
    }
  };

  const handleOpenHowTo = (event, gameId) => {
    event.preventDefault();
    event.stopPropagation();
    if (onOpenHowTo) {
      onOpenHowTo(gameId);
      return;
    }
    setHowToGameId(gameId);
    setActiveNav("howto");
  };

  const findMatchingGames = (query, { allowEmpty = false } = {}) => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return allowEmpty ? [...ALL_HOME_GAMES] : [];

    // Prefix-match on the displayed name only (e.g. "h" → Hangman, not Flashcards).
    return ALL_HOME_GAMES.filter((id) => {
      const name = t(GAMES[id].nameKey).toLowerCase();
      return name.startsWith(normalized);
    });
  };

  const searchSuggestions = findMatchingGames(searchValue);
  const showSearchSuggestions =
    searchFocused && !searchOpen && searchValue.trim().length > 0;

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    const matches = findMatchingGames(searchValue, { allowEmpty: true });
    setSearchMatches(matches);
    setSearchFocused(false);
    setSearchOpen(true);
  };

  const handleSelectSearchResult = (id) => {
    setSearchOpen(false);
    setSearchFocused(false);
    setSearchValue("");
    setSearchMatches([]);
    onSelectGame(id);
  };

  const handleSearchChange = (event) => {
    setSearchValue(event.target.value);
    setSearchFocused(true);
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === "Escape") {
      setSearchFocused(false);
      event.currentTarget.blur();
    }
  };

  const renderMainContent = () => {
    if (activeNav === "missions") {
      return <MissionsPage />;
    }

    if (activeNav === "rewards") {
      return <RewardsPage />;
    }

    if (activeNav === "howto") {
      return (
        <HowToPlayPage
          onSelectGame={onSelectGame}
          initialGameId={howToGameId}
        />
      );
    }

    if (activeNav === "pixels") {
      return <PixelsPage />;
    }

    if (activeNav === "languages") {
      return <LanguagesPage />;
    }

    if (activeNav === "leaderboard") {
      return (
        <section className="sw-leaderboards">
          <div className="sw-leaderboard-panel">
            <div className="sw-leaderboard-title">🦖 {t("dinoRun")}</div>
            <HighScores inline={true} showCurrentScore={false} />
          </div>
          <div className="sw-leaderboard-panel">
            <div className="sw-leaderboard-title">🧩 {t("maze")}</div>
            <MazeHighScores inline={true} />
          </div>
          <div className="sw-leaderboard-panel">
            <div className="sw-leaderboard-title">{t("solitaire")}</div>
            <SolitaireHighScores inline={true} />
          </div>
          <div className="sw-leaderboard-panel sw-leaderboard-panel--ops">
            <div className="sw-leaderboard-title">🚀 {t("opsTickles")}</div>
            <OpsTicklesHighScores />
          </div>
        </section>
      );
    }

    return (
      <>
        {renderHero()}
        <div className="sw-games-area">
          <div className="sw-games-column">
            <div className="sw-section-header sw-section-header--earn">
              <StarIcon size={14} solid />
              <span>{t("earnStars")}</span>
            </div>
            <div className="sw-earn-scroll-wrap">
              <button
                type="button"
                className="sw-earn-scroll-btn sw-earn-scroll-btn--left"
                onClick={() => scrollEarnGames(-1)}
                disabled={!canScrollEarnLeft}
                aria-label={t("scrollEarnPrev")}
              >
                <ChevronRightIcon />
              </button>
              <div
                ref={earnScrollRef}
                className="sw-game-grid sw-game-grid--earn-scroll"
              >
                {EARN_GAMES.map((id) => renderGameCard(id, "earn"))}
              </div>
              <button
                type="button"
                className="sw-earn-scroll-btn sw-earn-scroll-btn--right"
                onClick={() => scrollEarnGames(1)}
                disabled={!canScrollEarnRight}
                aria-label={t("scrollEarnNext")}
              >
                <ChevronRightIcon />
              </button>
            </div>

            <div className="sw-section-header sw-section-header--spend">
              <StarIcon size={14} solid />
              <span>{t("spendStars")}</span>
            </div>
            <div className="sw-game-grid">
              {SPEND_GAMES.map((id) => renderGameCard(id, "spend"))}
            </div>
          </div>

          <aside className="sw-rewards-card">
            <div className="sw-rewards-art" aria-hidden="true">
              <StarIcon3D size={120} />
            </div>
            <h3 className="sw-rewards-title">{t("keepGoing")}</h3>
            <p className="sw-rewards-text">{t("rewardsCardText")}</p>
            <button
              type="button"
              className="sw-rewards-btn"
              onClick={() => setActiveNav("rewards")}
            >
              {t("viewRewards")}
              <ChevronRightIcon />
            </button>
          </aside>
        </div>

        <div className="sw-stats-bar">
          <div className="sw-stat">
            <div className="sw-stat-icon sw-stat-icon--flame">
              <FlameIcon />
            </div>
            <div className="sw-stat-text">
              <div className="sw-stat-value">{dayStreak}</div>
              <div className="sw-stat-label">{t("dayStreak")}</div>
            </div>
          </div>
          <div className="sw-stat">
            <div className="sw-stat-icon sw-stat-icon--trophy">
              <GamesPlayedIcon />
            </div>
            <div className="sw-stat-text">
              <div className="sw-stat-value">{gamesPlayed}</div>
              <div className="sw-stat-label">{t("gamesPlayed")}</div>
            </div>
          </div>
          <div className="sw-stat">
            <div className="sw-stat-icon sw-stat-icon--star">
              <StarIcon size={20} solid />
            </div>
            <div className="sw-stat-text">
              <div className="sw-stat-value">{totalStars}</div>
              <div className="sw-stat-label">{t("totalStars")}</div>
            </div>
          </div>
          <div className="sw-stat sw-stat--reward">
            <div className="sw-stat-icon sw-stat-icon--gift">
              <GiftIcon />
            </div>
            <div className="sw-stat-text">
              <div className="sw-stat-value sw-stat-value--small">
                {t("nextReward")}
              </div>
              <div className="sw-stat-label">
                {starsToReward} {t("starsToGo")}
              </div>
              <div className="sw-stat-progress">
                <div
                  className="sw-stat-progress-fill"
                  style={{ width: `${rewardProgress}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  const renderHero = () => (
    <div className="sw-hero">
      <div className="sw-hero-text">
        <h1 className="sw-hero-title">
          {t("heroLine1")}
          <br />
          {t("heroLine2Pre")}{" "}
          <span className="sw-hero-grad sw-hero-grad--purple">
            {t("heroStars")}
          </span>
          .
          <br />
          {t("heroLine3Pre")}{" "}
          <span className="sw-hero-grad sw-hero-grad--cyan">
            {t("heroFun")}
          </span>
          .
        </h1>
        <p className="sw-hero-sub">
          {t("heroSubPre")}{" "}
          <span className="sw-hero-sub-accent sw-hero-sub-accent--purple">
            {t("heroSubReward")}
          </span>{" "}
          {t("heroSubMid")}
          <br />
          <span className="sw-hero-sub-accent sw-hero-sub-accent--pink">
            {t("heroSubCost")}
          </span>{" "}
          {t("heroSubEnd")}
        </p>
        <button
          type="button"
          className="sw-cta"
          onClick={handleStartPlaying}
        >
          <StarIcon size={16} solid />
          {t("startPlaying")}
        </button>
      </div>
    </div>
  );

  const renderGameCard = (id, accent) => {
    const game = GAMES[id];
    const meta = GAME_ICON_MAP[id];
    const Icon = meta.icon;
    const earns = game.earnPoints;
    const cost = game.price;
    const badge =
      game.badgeLabel ||
      (earns ? `+${game.earnAmount || 5}` : cost > 0 ? `-${cost}` : t("free"));

    return (
      <div key={id} className={`sw-game-card-wrap sw-game-card-wrap--${accent}`}>
        <button
          type="button"
          className="sw-game-howto-btn"
          onClick={(event) => handleOpenHowTo(event, id)}
          aria-label={`${t("howToCardHelp")}: ${t(game.nameKey)}`}
          title={t("howToCardHelp")}
        >
          ?
        </button>
        <button
          type="button"
          className={`sw-game-card sw-game-card--${accent}`}
          onClick={() => onSelectGame(id)}
        >
          <div className={`sw-game-icon sw-game-icon--${meta.accent}`}>
            <Icon />
          </div>
          <div className="sw-game-body">
            <div className="sw-game-header">
              <h3 className="sw-game-name">{t(game.nameKey)}</h3>
              <span className={`sw-game-badge sw-game-badge--${accent}`}>
                {badge}
                {!game.hideBadgeStar && <StarIcon size={12} solid />}
              </span>
            </div>
            <p className="sw-game-desc">{t(game.descriptionKey)}</p>
          </div>
          <span className="sw-game-arrow" aria-hidden="true">
            <ArrowRightIcon />
          </span>
        </button>
      </div>
    );
  };

  return (
    <div className="sw-home">
      <aside className="sw-sidebar">
        <div className="sw-sidebar-top">
          <div className="sw-brand">
            <StarworksLogo />
            <span className="sw-brand-name">STARWORKS</span>
          </div>

          <nav className="sw-nav" aria-label="Starworks">
            {navItems.map(({ id, icon: NavIcon, label }) => (
              <button
                key={id}
                type="button"
                className={`sw-nav-item ${activeNav === id ? "is-active" : ""}`}
                onClick={() => handleNavClick(id)}
                aria-label={label}
                title={label}
              >
                <NavIcon />
                <span>{label}</span>
              </button>
            ))}
          </nav>
        </div>
      </aside>

      <main className="sw-main">
        <header className="sw-topbar">
          <div className="sw-search-wrap" ref={searchRef}>
            <form
              className="sw-search"
              onSubmit={handleSearchSubmit}
              role="search"
            >
              <button
                type="submit"
                className="sw-search-submit"
                aria-label={t("searchGames")}
              >
                <SearchIcon />
              </button>
              <input
                type="search"
                placeholder={t("searchPlaceholder")}
                value={searchValue}
                onChange={handleSearchChange}
                onFocus={() => setSearchFocused(true)}
                onKeyDown={handleSearchKeyDown}
                enterKeyHint="search"
                autoComplete="off"
                role="combobox"
                aria-autocomplete="list"
                aria-expanded={showSearchSuggestions}
                aria-controls="sw-search-suggest"
              />
            </form>

            {showSearchSuggestions && (
              <ul
                id="sw-search-suggest"
                className="sw-search-suggest"
                role="listbox"
              >
                {searchSuggestions.length === 0 ? (
                  <li className="sw-search-suggest-empty">
                    {t("searchNoResults")}
                  </li>
                ) : (
                  searchSuggestions.map((id) => {
                    const game = GAMES[id];
                    const name = t(game.nameKey);
                    return (
                      <li key={id} role="option" aria-selected={false}>
                        <button
                          type="button"
                          className="sw-search-suggest-item"
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => handleSelectSearchResult(id)}
                        >
                          {highlightMatch(name, searchValue)}
                        </button>
                      </li>
                    );
                  })
                )}
              </ul>
            )}
          </div>

          <div className="sw-topbar-actions">
            <div className="sw-stars-pill">
              <span className="sw-stars-pill-icon">
                <StarIcon size={20} solid />
              </span>
              <span className="sw-stars-pill-value">{points}</span>
            </div>

            <div className="sw-avatar-menu" ref={menuRef}>
              <button
                type="button"
                className={`sw-display-name-btn ${menuOpen ? "is-open" : ""}`}
                onClick={() => setMenuOpen((open) => !open)}
                aria-label={t("profileMenu")}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                title={displayName || t("profileMenu")}
              >
                <span className="sw-display-name-avatar" aria-hidden="true">
                  <AvatarIcon />
                </span>
              </button>

              {menuOpen && (
                <div className="sw-avatar-dropdown" role="menu">
                  <button
                    type="button"
                    className="sw-avatar-dropdown-item"
                    role="menuitem"
                    onClick={() => {
                      setMenuOpen(false);
                      onEditDisplayName();
                    }}
                  >
                    {t("editDisplayName")}
                  </button>
                  <button
                    type="button"
                    className="sw-avatar-dropdown-item"
                    role="menuitem"
                    onClick={() => {
                      onToggleMusic();
                    }}
                  >
                    {musicPlaying ? t("muteMusic") : t("playMusic")}
                  </button>
                  <button
                    type="button"
                    className="sw-avatar-dropdown-item"
                    role="menuitem"
                    onClick={() => {
                      onChangeLanguage(language === "en" ? "de" : "en");
                    }}
                  >
                    {t("changeLanguage")}: {language === "en" ? "DE" : "EN"}
                  </button>
                  <div className="sw-mode-menu">
                    <button
                      type="button"
                      className={`sw-avatar-dropdown-item sw-mode-menu-trigger ${
                        modeMenuOpen ? "is-open" : ""
                      }`}
                      role="menuitem"
                      aria-haspopup="true"
                      aria-expanded={modeMenuOpen}
                      onClick={() => setModeMenuOpen((open) => !open)}
                    >
                      <span className="sw-mode-menu-trigger-main">
                        <span
                          className="sw-mode-rainbow-dot"
                          aria-hidden="true"
                        />
                        <span>
                          {t("changeMode")}: {t(MODE_LABEL_KEYS[mode])}
                        </span>
                      </span>
                      <ChevronDownIcon />
                    </button>
                    {modeMenuOpen && (
                      <div
                        className="sw-mode-submenu"
                        role="group"
                        aria-label={t("changeMode")}
                      >
                        {modes.map((modeId) => (
                          <button
                            key={modeId}
                            type="button"
                            className={`sw-mode-option ${
                              mode === modeId ? "is-active" : ""
                            } ${modeId === "yellow" ? "is-light" : ""}`}
                            style={{ "--swatch-color": swatches[modeId] }}
                            aria-pressed={mode === modeId}
                            onClick={() => {
                              changeMode(modeId);
                              setModeMenuOpen(false);
                            }}
                          >
                            <span
                              className="sw-mode-option-swatch"
                              aria-hidden="true"
                            />
                            <span>{t(MODE_LABEL_KEYS[modeId])}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    className="sw-avatar-dropdown-item"
                    role="menuitem"
                    onClick={openShipments}
                  >
                    {t("myShipments")}
                  </button>
                  <button
                    type="button"
                    className="sw-avatar-dropdown-item"
                    role="menuitem"
                    onClick={openProblemReport}
                  >
                    {t("reportProblem")}
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <section className="sw-content">{renderMainContent()}</section>
      </main>

      {searchOpen && (
        <div
          className="sw-shipments-overlay"
          onClick={() => setSearchOpen(false)}
        >
          <div
            className="sw-shipments-modal sw-search-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="search-modal-title"
          >
            <div className="sw-shipments-header">
              <div>
                <h3 id="search-modal-title">{t("searchResultsTitle")}</h3>
                <p className="sw-shipments-sub">
                  {searchValue.trim()
                    ? t("searchResultsSubtitle").replace(
                        "{query}",
                        searchValue.trim()
                      )
                    : t("searchResultsHint")}
                </p>
              </div>
              <button
                type="button"
                className="sw-shipments-close"
                onClick={() => setSearchOpen(false)}
              >
                {t("close")}
              </button>
            </div>

            {searchMatches.length === 0 ? (
              <p className="sw-shipments-muted">{t("searchNoResults")}</p>
            ) : (
              <ul className="sw-search-results">
                {searchMatches.map((id) => {
                  const game = GAMES[id];
                  const meta = GAME_ICON_MAP[id];
                  const Icon = meta.icon;
                  const earns = game.earnPoints;
                  const badge = earns
                    ? `+${game.earnAmount || 5}`
                    : `-${game.price}`;

                  return (
                    <li key={id}>
                      <button
                        type="button"
                        className="sw-search-result"
                        onClick={() => handleSelectSearchResult(id)}
                      >
                        <div
                          className={`sw-game-icon sw-game-icon--${meta.accent}`}
                        >
                          <Icon />
                        </div>
                        <div className="sw-search-result-body">
                          <div className="sw-search-result-header">
                            <span className="sw-search-result-name">
                              {t(game.nameKey)}
                            </span>
                            <span
                              className={`sw-game-badge sw-game-badge--${
                                earns ? "earn" : "spend"
                              }`}
                            >
                              {badge}
                              <StarIcon size={12} />
                            </span>
                          </div>
                          <p className="sw-search-result-desc">
                            {t(game.descriptionKey)}
                          </p>
                        </div>
                        <span className="sw-game-arrow" aria-hidden="true">
                          <ArrowRightIcon />
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}

      {shipmentsOpen && (
        <div
          className="sw-shipments-overlay"
          onClick={() => setShipmentsOpen(false)}
        >
          <div
            className="sw-shipments-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="shipments-modal-title"
          >
            <div className="sw-shipments-header">
              <div>
                <h3 id="shipments-modal-title">{t("myShipments")}</h3>
                <p className="sw-shipments-sub">{t("myShipmentsSubtitle")}</p>
              </div>
              <button
                type="button"
                className="sw-shipments-close"
                onClick={() => setShipmentsOpen(false)}
              >
                {t("close")}
              </button>
            </div>

            {shipmentsLoading && (
              <p className="sw-shipments-muted">{t("loading")}</p>
            )}
            {shipmentsError && (
              <p className="sw-shipments-error">{shipmentsError}</p>
            )}
            {!shipmentsLoading &&
              !shipmentsError &&
              shipments.length === 0 && (
                <p className="sw-shipments-muted">{t("shipmentsEmpty")}</p>
              )}

            {!shipmentsLoading && shipments.length > 0 && (
              <ul className="sw-shipments-list">
                {shipments.map((claim) => (
                  <li key={claim.id} className="sw-shipment-item">
                    <div className="sw-shipment-main">
                      <div className="sw-shipment-name">
                        {claim.rewardName || claim.rewardId || "—"}
                      </div>
                      <div className="sw-shipment-meta">
                        <span className="sw-shipment-color">
                          <span
                            className={`sw-shipment-swatch ${
                              isFilamentLight(claim.color) ? "is-light" : ""
                            } ${
                              isFilamentRainbow(claim.color) ? "is-rainbow" : ""
                            }`}
                            style={
                              isFilamentRainbow(claim.color)
                                ? undefined
                                : {
                                    backgroundColor: getFilamentHex(claim.color),
                                  }
                            }
                            aria-hidden="true"
                          />
                          {getFilamentLabel(claim.color)}
                        </span>
                        <span className="sw-shipment-date">
                          {formatClaimDate(claim.createdAt)}
                        </span>
                      </div>
                    </div>
                    <div className="sw-shipment-side">
                      <span
                        className={`sw-shipment-status sw-shipment-status--${
                          claim.status || "pending"
                        }`}
                      >
                        {statusLabel(claim.status)}
                      </span>
                      <button
                        type="button"
                        className="sw-shipment-delete"
                        disabled={deletingShipmentId === claim.id}
                        onClick={() => handleDeleteShipment(claim)}
                      >
                        {deletingShipmentId === claim.id
                          ? t("shipmentDeleting")
                          : t("shipmentDelete")}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {reportOpen && (
        <div
          className="sw-shipments-overlay"
          onClick={() => {
            if (!reportSending) setReportOpen(false);
          }}
        >
          <div
            className="sw-shipments-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="report-modal-title"
          >
            <div className="sw-shipments-header">
              <div>
                <h3 id="report-modal-title">{t("reportProblem")}</h3>
                <p className="sw-shipments-sub">{t("reportProblemSubtitle")}</p>
              </div>
              <button
                type="button"
                className="sw-shipments-close"
                onClick={() => setReportOpen(false)}
                disabled={reportSending}
              >
                {t("close")}
              </button>
            </div>

            <form className="sw-report-form" onSubmit={handleSubmitProblemReport}>
              <label className="sw-report-label" htmlFor="problem-report-message">
                {t("reportProblemMessageLabel")}
              </label>
              <textarea
                id="problem-report-message"
                className="sw-report-textarea"
                value={reportMessage}
                onChange={(e) => {
                  setReportMessage(e.target.value);
                  setReportError("");
                  setReportSuccess("");
                }}
                placeholder={t("reportProblemPlaceholder")}
                rows={5}
                maxLength={2000}
                disabled={reportSending}
                autoFocus
              />
              <div className="sw-report-meta">
                <span>{reportMessage.length}/2000</span>
              </div>
              {reportError && (
                <p className="sw-shipments-error">{reportError}</p>
              )}
              {reportSuccess && (
                <p className="sw-report-success">{reportSuccess}</p>
              )}
              <button
                type="submit"
                className="sw-report-send"
                disabled={reportSending || !reportMessage.trim()}
              >
                {reportSending
                  ? t("reportProblemSending")
                  : t("reportProblemSend")}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomeScreen;
